import { Injectable, Injector } from '@angular/core';
import { SessionHelper } from '../helper/sessionHelper';
import { Router } from '@angular/router';
import { AuthService } from '../services/acceso/auth.service';
import {
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, from } from 'rxjs';
import { switchMap, take, filter, tap, catchError, finalize } from 'rxjs/operators';
import { ErrorApi, ErrorLogin } from '../models/errorApi.model';
import { Token } from '../models/token.model';
import { Configuration } from '../configuration/configuration';
import { ErrorPage } from '../models/errorPage.model';
import { AppConfig } from '../configuration/app.config';
import { AppSecurityService } from '../services/appSecurity.service';
import { LivesSessionHelper } from '../helper/livesSessionHelper';
/**
 * @description
 * Interceptor HTTP con el que añadir las cabeceras comunes a las peticiones de la api rest
 */
@Injectable()
export class CustomInterceptor implements HttpInterceptor {
  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    protected _session: SessionHelper,
    protected _rotuer: Router,
    protected _configuration: Configuration,
    private _injector: Injector,
    private _livesSession: LivesSessionHelper,
  ) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {


    const isLivesHRApi = AppConfig.settings?.apiServerPE?.apiUrl
      ? req.url.includes(AppConfig.settings.apiServerPE.apiUrl)
      : false;

    const excludedEndpoints = [ //Excluir ciertos endpoints de Lives HR y A3DocCloud
      'auth/management',
      'auth/refresh',
      'auth/initialize/web'
    ];

    const isExcluded = excludedEndpoints.some(endpoint => req.url.includes(endpoint));
    if (isExcluded) {
      req = req.clone({ withCredentials: true });
      return next.handle(req);
    }

    // Cabecera Authorization
    if (isLivesHRApi) {
      req = this.addBearerToken(req, this._livesSession.LivesAccessToken);
    }
    else {
      req = this.addBearerToken(req, this._session.AccessToken);
    }


    let errorApi: ErrorApi | ErrorLogin;

    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.error instanceof Error) {
          // Error del lado del cliente.
          console.error(
            'Se ha producido un error del lado del cliente:', err.error.message
          );
        } else {

          // Error del lado del servidor.
          let descripcion: string = err.error;

          if (ErrorApi.instanceOfErrorApi(err.error)) {
            errorApi = err.error as ErrorApi;
            descripcion = errorApi.ErrorDescription.toString();
          }
          if (ErrorLogin.instanceOfErrorLogin(err.error)) {
            errorApi = err.error as ErrorLogin;
            descripcion = errorApi.error_description;
          }
          else {
            try {
              descripcion = err.error.ExceptionMessage;
            } catch (error) {
              // supress error;
            }
          }

          console.error(
            `El backend ha devuelto el error ${err.status}, body: ${descripcion}`
          );

          switch (err.status) {
            case 401:
              if (isLivesHRApi) {
                return this.handle401ErrorLives(req, next)
              } else {
                return this.handle401Error(req, next);
              }
            case 403: //
              return this.logout();
          }
        }

        return throwError(
          errorApi
            ? errorApi
            : new Error('Se ha producido un error en la solicitud a la API')
        );
      }));
  }

  logout() {
    this._rotuer.navigate([Configuration.loginUrl]);
    this._session.Delete();
    return throwError('');
  }

  addBearerToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
  }

  handle401Error(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshingToken) {

      this.isRefreshingToken = true;
      this.tokenSubject.next(null);

      const auth = this._injector.get(AuthService);
      const refreshToken = auth.refreshToken(this._session.RefreshToken);

      return refreshToken
        .pipe(
          switchMap((newToken: Token) => {
            // console.log(newToken);
            this.tokenSubject.next(newToken.access_token);
            this._session.AccessToken = newToken.access_token;
            this._session.AccessTokenExpireIn = newToken['.expires'];
            this._session.RefreshToken = newToken.refresh_token;
            return next.handle(
              this.addBearerToken(req, this._session.AccessToken
              )
            );
          }),
          catchError(err => {
            console.error(err);
            return this.logout();
          }),
          finalize(() => {
            this.isRefreshingToken = false;
          })
        );
    } else {
      return this.tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          return next.handle(this.addBearerToken(req, token));
        }));
    }
  }

  private handle401ErrorLives(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.error('401 en API LivesHR');
    const appSecurityService = this._injector.get(AppSecurityService);
    const user = this._session.User;
    const clientId = user.IdCliente;

    return from(this._livesSession.waitForAspNetUserId()).pipe(
      switchMap(aspNetUserId => {
        return appSecurityService.renewToken(clientId, aspNetUserId).pipe(
          switchMap(newToken => {
            this._livesSession.LivesAccessToken = newToken;

            const clonedRequest = this.addBearerToken(req, newToken);
            return next.handle(clonedRequest);
          }),
          catchError(err => {
            console.error('[Interceptor] Fallo total de renovación LivesHR');
            return this.logout();
          })
        );
      })
    );
  }


  private getCookie(name: string): string {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : '';
  }


}
